import React from "react";
import { useNavigate } from "react-router-dom";
import type { IconType } from "react-icons";
import {
  HiCheckCircle,
  HiOutlineBanknotes,
  HiOutlineBuildingStorefront,
  HiOutlineCalculator,
  HiOutlineChatBubbleLeftRight,
  HiOutlineClipboardDocumentList,
  HiOutlineCurrencyDollar,
  HiOutlineDocumentChartBar,
  HiOutlineLockClosed,
  HiOutlineMegaphone,
  HiOutlinePresentationChartLine,
  HiOutlinePuzzlePiece,
  HiOutlineShoppingCart,
  HiOutlineTruck,
} from "react-icons/hi2";
import styles from "./LockedFeatureCard.module.css";
import {
  getFeatureCopy,
  type FeatureIconKey,
} from "../PlanGate/featureCopy";
import { monthlyNetOf, usePlanOffer } from "../PlanGate/usePlanOffer";
import { formatMoney } from "../../modules/account/utilities/accountFormat";
import { ROUTES } from "../../modules/account/models/routes";

const ICONS: Record<FeatureIconKey, IconType> = {
  compras: HiOutlineShoppingCart,
  marketing: HiOutlineMegaphone,
  analitica: HiOutlinePresentationChartLine,
  pedidos: HiOutlineTruck,
  cobrar: HiOutlineBanknotes,
  cambio: HiOutlineCurrencyDollar,
  comisiones: HiOutlineCalculator,
  integraciones: HiOutlinePuzzlePiece,
  reportes: HiOutlineDocumentChartBar,
  sedes: HiOutlineBuildingStorefront,
  whatsapp: HiOutlineChatBubbleLeftRight,
  comandas: HiOutlineClipboardDocumentList,
};

interface LockedFeatureCardProps {
  /** Clave de la feature gateada (FEATURE_GATES del backend). */
  feature: string;
}

/**
 * Pantalla de bloqueo ÚNICA por plan (spec 007): la misma para todos los
 * módulos gateados. Vende el plan que incluye la función: beneficio concreto,
 * precio neto del servidor y checkout directo sin salir de la pantalla.
 * Quien no es administrador no puede comprar: se le indica a quién pedírselo.
 */
export const LockedFeatureCard: React.FC<LockedFeatureCardProps> = ({ feature }) => {
  const navigate = useNavigate();
  const copy = getFeatureCopy(feature);
  const { offer, buyPlan } = usePlanOffer();
  const Icon = ICONS[copy.icon] ?? HiOutlineLockClosed;
  const price = monthlyNetOf(offer, copy.requiredPlan);

  return (
    <div className={styles.wrapper}>
      <section
        className={styles.card}
        aria-labelledby="locked-feature-title"
        data-testid="locked-feature-card"
      >
        <div className={styles.iconWrap} aria-hidden>
          <Icon size={34} />
          <span className={styles.lockBadge}>
            <HiOutlineLockClosed size={14} />
          </span>
        </div>

        <span className={styles.chip}>
          {copy.name} · Incluido en el plan {copy.requiredPlan}
        </span>

        <h2 id="locked-feature-title" className={styles.title}>
          {copy.title}
        </h2>
        <p className={styles.subtitle}>{copy.subtitle}</p>

        <ul className={styles.bullets}>
          {copy.bullets.map((bullet) => (
            <li key={bullet} className={styles.bullet}>
              <HiCheckCircle size={20} className={styles.bulletIcon} aria-hidden />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>

        <div className={styles.footer}>
          {price != null && (
            <p className={styles.price} data-testid="locked-feature-price">
              Plan {copy.requiredPlan} desde{" "}
              <strong>{formatMoney(price)}</strong> {offer.taxSuffix} / mes
            </p>
          )}

          {offer.canPurchase ? (
            <button
              type="button"
              className={styles.primary}
              onClick={() => buyPlan(copy.requiredPlan)}
            >
              Pasar a {copy.requiredPlan}
            </button>
          ) : (
            <p className={styles.adminHint} role="note">
              Pídele al administrador de tu negocio que active el plan{" "}
              {copy.requiredPlan}.
            </p>
          )}

          <button
            type="button"
            className={styles.secondary}
            onClick={() => navigate(ROUTES.AVAILABLE_PLANS_ROUTE)}
          >
            Comparar planes
          </button>
        </div>
      </section>
    </div>
  );
};

export default LockedFeatureCard;
